import React, { useEffect, useState, useCallback } from "react";
import { Bell, MessageCircle, Mail, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function NotificationBell() {
  const [data, setData] = useState({ new_comments: 0, new_digests: 0, total: 0, recent_comments: [], recent_digests: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/summary");
      setData(data);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 45_000);
    return () => clearInterval(id);
  }, [load]);

  const markRead = async (kind) => {
    setLoading(true);
    try {
      await api.post("/notifications/mark-read", { kind });
      await load();
    } finally {
      setLoading(false);
    }
  };

  const total = data.total || 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell-btn"
          aria-label="Notifications"
          className="relative p-2 border border-hairline text-bone/70 hover:text-bone hover:bg-surface"
        >
          <Bell className="w-4 h-4" />
          {total > 0 && (
            <span
              data-testid="notifications-bell-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-safety text-white text-[10px] font-display font-black flex items-center justify-center leading-none"
            >
              {total > 99 ? "99+" : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        data-testid="notifications-popover"
        className="w-[380px] rounded-none bg-ink border border-hairline text-bone p-0 max-h-[70vh] overflow-y-auto scrollbar-thin"
      >
        <div className="px-4 py-3 border-b border-hairline flex items-center justify-between">
          <div className="font-display font-bold tracking-tightest text-bone">Notifications</div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => markRead("all")}
            disabled={loading || total === 0}
            data-testid="notifications-mark-all-read"
            className="rounded-none label-mono text-[10px] text-bone/60 hover:text-bone hover:bg-surface h-7"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Mark all read"}
          </Button>
        </div>

        <Section
          icon={MessageCircle}
          title="Reviewer comments"
          count={data.new_comments}
          empty="No new reviewer comments."
          testid="notif-section-comments"
        >
          {(data.recent_comments || []).map((c) => (
            <div key={c.id} className="px-4 py-3 border-b border-hairline/60" data-testid={`notif-comment-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="label-mono text-[10px] text-bone/50">{(c.reviewer_name || "Anon")}</div>
                <div className="label-mono text-[10px] text-bone/40">{String(c.created_at).slice(0, 16)}</div>
              </div>
              <div className="text-sm text-bone/90 mt-1 line-clamp-3">{c.comment}</div>
              <div className="label-mono text-[10px] text-bone/40 mt-1.5 line-clamp-1">on: {c.article_title}</div>
            </div>
          ))}
        </Section>

        <Section
          icon={Mail}
          title="Daily digests"
          count={data.new_digests}
          empty="No new digests."
          testid="notif-section-digests"
        >
          {(data.recent_digests || []).map((d) => (
            <div key={d.id} className="px-4 py-3 border-b border-hairline/60" data-testid={`notif-digest-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="label-mono text-[10px] text-bone/50">{String(d.created_at).slice(0, 16)}</div>
                <div className="label-mono text-[10px] text-bone/40">{d.emailed ? "emailed" : "stored"}</div>
              </div>
              <ul className="mt-1.5 list-disc list-outside ml-4 text-sm text-bone/85 space-y-0.5">
                {(d.topics || []).slice(0, 3).map((t, i) => (
                  <li key={i} className="line-clamp-2">{t}</li>
                ))}
              </ul>
            </div>
          ))}
        </Section>
      </PopoverContent>
    </Popover>
  );
}

const Section = ({ icon: Icon, title, count, empty, children, testid }) => {
  const has = React.Children.count(children) > 0;
  return (
    <div data-testid={testid}>
      <div className="px-4 py-2.5 bg-surface flex items-center gap-2 border-b border-hairline">
        <Icon className="w-3.5 h-3.5 text-safety" />
        <div className="label-mono text-[10px] text-bone/70 flex-1">{title}</div>
        {count > 0 && (
          <span className="label-mono text-[10px] text-safety">{count} new</span>
        )}
      </div>
      {has ? children : <div className="px-4 py-4 text-xs text-bone/40 label-mono">{empty}</div>}
    </div>
  );
};
